import { useMemo } from 'react'
import { X, Plus, Minus } from 'lucide-react'
import { cn } from '@utils'
import { StepCategory } from '@/constants'
import { useSessionStore } from '@/stores'
import { useUIStore } from '@/stores'
import { updateFontSizes } from '@/utils/fontSizing'
import { parseStyleProp } from '@/utils/dom'

interface VariantRow {
  id: string
  quantity: number
  options: Record<string, string>
  key: string
}

interface SelectedQuantities {
  [id: string]: {
    quantity: number
    options: Record<string, string>
    key: string
  }
}

interface VariantQuantitiesProps {
  selectedQuantities: SelectedQuantities
  totalQuantity: number
  onChange?: (selectedQuantities: SelectedQuantities) => void
  onSelect?: (selectedQuantities: SelectedQuantities) => void
  onBack?: () => void
}

const titleCase = (str: string) => str.toLowerCase().replace(/\w\S*/g, w => w.replace(/^\w/, c => c.toUpperCase()))

const VariantQuantities = ({
  selectedQuantities: initialQuantities,
  totalQuantity,
  onChange,
  onSelect,
  onBack,
}: VariantQuantitiesProps) => {
  const session = useSessionStore(state => state.session)
  const addUIMessage = useUIStore(state => state.addUIMessage)

  const emit = (next: SelectedQuantities) => {
    onChange?.(next)
  }

  const showStepLabels = useMemo(() => (session as any)?.step_category === StepCategory.ChangeVariant, [session])
  const showBackButton = !!session?.can_undo || !!(session as any)?.is_step_preview

  const totalSelected = useMemo(() => {
    let total = 0
    for (const row of Object.values(initialQuantities)) {
      const q = parseInt(String(row.quantity))
      total += isNaN(q) ? 0 : q
    }
    return total
  }, [initialQuantities])

  const { rows, columns } = useMemo(() => {
    const optionNames: Record<string, boolean> = {}
    const rowList: VariantRow[] = []

    for (const [id, row] of Object.entries(initialQuantities)) {
      rowList.push({ id, ...row })
      Object.keys(row.options).forEach(name => {
        optionNames[name] = true
      })
    }

    return {
      rows: rowList.sort((a, b) => a.key.localeCompare(b.key)),
      columns: Object.keys(optionNames).sort(),
    }
  }, [initialQuantities])

  const clearQuantity = (variantId: string) => {
    const next = {
      ...initialQuantities,
      [variantId]: { ...initialQuantities[variantId], quantity: 0 },
    }
    emit(next)
  }

  const setRowQuantity = (variantId: string, quantity: number) => {
    emit({
      ...initialQuantities,
      [variantId]: { ...initialQuantities[variantId], quantity: Math.max(0, quantity) },
    })
  }

  const done = () => {
    let total = 0
    for (const row of Object.values(initialQuantities)) {
      const q = parseInt(String(row.quantity))
      total += isNaN(q) ? 0 : q
    }

    if (total !== totalQuantity) {
      addUIMessage({
        type: 'error',
        message: `Total quantity must be ${totalQuantity}`,
        persistent: false,
      })
      return
    }

    onSelect?.(initialQuantities)
  }

  return (
    <div>
      <div className="w-full max-w-2xl mx-auto px-2 lg:px-4 overflow-x-auto">
        <table className="variant-quantities-table">
          <thead>
            <tr>
              {columns.map(name => (
                <th key={`column-${name}`}>{titleCase(name)}</th>
              ))}
              <th className="qty-col">Quantity</th>
              <th className="action-col"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const qty = initialQuantities[row.id]?.quantity ?? 0
              const optionLabel = columns.map(name => row.options[name]).join(' ')
              const atCapacity = totalSelected >= totalQuantity
              return (
                <tr key={`row-${row.id}`}>
                  {columns.map(name => (
                    <td key={`cell-${row.id}-${name}`} className="variant-option-cell">
                      {row.options[name]}
                    </td>
                  ))}
                  <td className="qty-col">
                    <div className="qty-stepper">
                      <button
                        type="button"
                        className="qty-step-btn no-transform"
                        aria-label={`Decrease ${optionLabel}`}
                        disabled={qty <= 0}
                        onClick={() => setRowQuantity(row.id, qty - 1)}>
                        <Minus size={18} aria-hidden="true" />
                      </button>
                      <span className="qty-step-value" aria-live="polite">
                        {qty}
                      </span>
                      <button
                        type="button"
                        className="qty-step-btn no-transform"
                        aria-label={`Increase ${optionLabel}`}
                        disabled={atCapacity}
                        onClick={() => setRowQuantity(row.id, qty + 1)}>
                        <Plus size={18} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                  <td className="action-col">
                    <button
                      type="button"
                      className="clear-btn no-transform"
                      aria-label={`Clear ${optionLabel}`}
                      disabled={qty <= 0}
                      onClick={() => clearQuantity(row.id)}>
                      <X size={16} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <div className="variant-quantity-summary text-fluid-sm">
          Total selected: {totalSelected} / {totalQuantity}
        </div>
      </div>

      {showStepLabels ? (
        <div className="flex flex-col items-center gap-3 mt-4 w-full">
          <div className="w-full md:w-7/12 flex flex-col items-center gap-2">
            {(session?.valid_inputs || []).map((input: any) => (
              <button
                key={`choice-${input.id}`}
                type="button"
                className="btn-base input-choice-btn input-btn w-full"
                style={parseStyleProp(input.button_styles?.style)}
                onClick={done}>
                <div dangerouslySetInnerHTML={{ __html: updateFontSizes(input.formatted_label || '') }} />
              </button>
            ))}
            <button type="button" className="btn-base back-btn w-full" onClick={() => onBack?.()}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-center items-center gap-3 mt-4 w-full">
          {showBackButton && (
            <div className="w-1/2 md:w-1/3">
              <button type="button" className="btn-base back-btn w-full" onClick={() => onBack?.()}>
                Back
              </button>
            </div>
          )}
          <div className={cn(showBackButton ? 'w-1/2 md:w-1/3' : 'w-full md:w-2/3')}>
            <button type="button" className="btn-base input-choice-btn w-full" onClick={done}>
              Continue &rsaquo;
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default VariantQuantities
